import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Menu as MenuIcon, Eye } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { NavigationBar, NavigationConfigs } from '@/components/layout';
import { EmptyState } from '@/components/ui/EmptyState';
import { SectionCard } from './SectionCard';
import { SectionModal } from './SectionModal';
import { MenuItemList } from './MenuItemList';
import useMenuStore from '@/lib/stores/menuStore';
import type { MenuSection, Restaurant, MenuSectionFormData } from '@/types';

interface MenuBuilderProps {
  restaurant: Restaurant;
  onBack: () => void;
}

/**
 * MenuBuilder - Main menu building interface
 * Handles sections management and provides foundation for item management
 */
export const MenuBuilder: React.FC<MenuBuilderProps> = ({
  restaurant,
  onBack,
}) => {
  const {
    sections,
    items,
    loading,
    error,
    reorderingSections,
    setCurrentRestaurant,
    fetchMenu,
    addSection,
    updateSection,
    deleteSection,
    reorderSections,
    clearError,
    getSectionItemCount,
  } = useMenuStore();

  const [showSectionModal, setShowSectionModal] = useState(false);
  const [editingSection, setEditingSection] = useState<MenuSection | undefined>();
  const [activeSection, setActiveSection] = useState<MenuSection | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Initialize menu data when component mounts
  useEffect(() => {
    const initializeMenu = async () => {
      setCurrentRestaurant(restaurant);
      await fetchMenu(restaurant.id);
      setInitialLoading(false);
    };
    initializeMenu();
  }, [restaurant, setCurrentRestaurant, fetchMenu]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleAddSection = () => {
    setEditingSection(undefined);
    setShowSectionModal(true);
  };

  const handleEditSection = (section: MenuSection) => {
    setEditingSection(section);
    setShowSectionModal(true);
  };

  const handleSaveSection = async (data: MenuSectionFormData) => {
    if (editingSection) {
      await updateSection(editingSection.id, data);
    } else {
      await addSection(data);
    }
  };

  const handleDeleteSection = async (section: MenuSection) => {
    await deleteSection(section.id);
  };

  const handleManageItems = (section: MenuSection) => {
    setActiveSection(section);
  };

  const handleBackToSections = () => {
    setActiveSection(null);
  };

  const handlePreviewMenu = () => {
    const previewUrl = `/menu/${restaurant.id}`;
    window.open(previewUrl, '_blank');
  };

  const handleReorderSections = async (reorderedSections: MenuSection[]) => {
    // Update order property for each section
    const sectionsWithUpdatedOrder = reorderedSections.map((section, index) => ({
      ...section,
      order: index
    }));
    
    try {
      await reorderSections(sectionsWithUpdatedOrder);
    } catch (error) {
      console.error('Failed to reorder sections:', error);
    }
  };

  const getItemCountForSection = (sectionId: string): number => {
    return getSectionItemCount(sectionId);
  };

  const existingSectionNames = sections
    .filter(s => s.id !== editingSection?.id)
    .map(s => s.name);

  // Get section items directly - no need to memoize since filtering is fast
  const sectionItems = activeSection ? items.filter(item => item.sectionId === activeSection.id).sort((a, b) => a.order - b.order) : [];

  // Show item management for active section
  if (activeSection) {
    
    const previewButton = (
      <Button
        variant="ghost"
        size="sm"
        onClick={handlePreviewMenu}
        leftIcon={<Eye className="w-4 h-4" />}
        disabled={sections.length === 0}
      >
        Preview
      </Button>
    );
    
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Navigation */}
        <NavigationBar 
          config={NavigationConfigs.menuItemList(
            activeSection.name,
            restaurant.name,
            handleBackToSections,
            previewButton
          )} 
        />

        {/* Main Content */}
        <main className="max-w-12xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          <MenuItemList
            section={activeSection}
            items={sectionItems}
            sections={sections}
          />
        </main>
      </div>
    );
  }  const previewButton = (
    <Button
      variant="ghost"
      size="sm"
      onClick={handlePreviewMenu}
      leftIcon={<Eye className="w-4 h-4" />}
      disabled={sections.length === 0}
    >
      Preview
    </Button>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <NavigationBar 
        config={NavigationConfigs.menuBuilder(
          restaurant.name,
          onBack,
          previewButton
        )} 
      />
      
      {/* Main Content */}
      <main className="max-w-12xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Menu Sections</h2>
            <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">
              Organize your menu into sections like appetizers, mains, and beverages
            </p>
          </div>
          
          <Button
            variant="primary"
            onClick={handleAddSection}
            leftIcon={<Plus className="w-4 h-4" />}
            disabled={loading || initialLoading}
            className="w-full sm:w-auto"
          >
            Add Section
          </Button>
        </div>

        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
          >
            <p className="text-red-800 text-sm font-medium">
              {error}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearError}
              className="mt-2"
            >
              Dismiss
            </Button>
          </motion.div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-8 sm:py-12">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 animate-pulse">
              <MenuIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <p className="text-gray-600 text-sm sm:text-base">Loading your menu...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && sections.length === 0 && (
          <EmptyState
            icon={<MenuIcon className="w-8 h-8 sm:w-12 sm:h-12 text-white" />}
            title="Ready to build your menu?"
            description="Start by creating sections to organize your dishes. Add sections like Appetizers, Main Course, Beverages, and Desserts to structure your menu."
          />
        )}

        {/* Sections Grid - Now with Arrow Controls */}
        {!loading && sections.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6"
          >
            {sections.map((section) => (
              <SectionCard
                key={section.id}
                section={section}
                itemCount={getItemCountForSection(section.id)}
                onEdit={handleEditSection}
                onDelete={handleDeleteSection}
                onManageItems={handleManageItems}
                allSections={sections}
                onReorderSections={handleReorderSections}
                isReordering={reorderingSections.includes(section.id)}
                isAnyReordering={reorderingSections.length > 0}
              />
            ))}
          </motion.div>
        )}
        {/* Menu Stats */}
        {!loading && sections.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 p-4 bg-white rounded-xl border border-gray-200"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Menu Overview</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-primary">{sections.length}</p>
                <p className="text-sm text-gray-600">
                  {sections.length === 1 ? 'Section' : 'Sections'}
                </p>
              </div>
              <div>
                <p className="text-2xl font-bold text-apple-green">{items.length}</p>
                <p className="text-sm text-gray-600">
                  {items.length === 1 ? 'Item' : 'Items'}
                </p>
              </div>
              <div>
                <p className="text-2xl font-bold text-accent">
                  {sections.filter(s => s.isVisible).length}
                </p>
                <p className="text-sm text-gray-600">Visible</p>
              </div>
            </div>
          </motion.div>
        )}
      </main>

      {/* Section Modal */}
      <SectionModal
        isOpen={showSectionModal}
        onClose={() => setShowSectionModal(false)}
        onSave={handleSaveSection}
        section={editingSection}
        existingSectionNames={existingSectionNames}
      />
    </div>
  );
};